/**
 * Google Analytics 4 loader — only injects gtag.js once consent is accepted.
 *
 * Consent is persisted in localStorage under `v_analytics_consent`:
 *   - "accepted"  → GA loads
 *   - "declined"  → GA never loads in this browser
 *   - missing     → banner is shown, no tracking yet
 *
 * Privacy notes:
 *   - We never push email/user-id to GA. Tracking is anonymous (page paths only).
 *   - SPA route changes fire `page_view` events via gtag('event', 'page_view').
 *   - If REACT_APP_GA_MEASUREMENT_ID is unset, the loader is a no-op (safe to
 *     ship while the GA property is still being configured).
 */

const STORAGE_KEY = "v_analytics_consent";
let _loaded = false;

export function getConsent() {
  try { return localStorage.getItem(STORAGE_KEY) || ""; } catch { return ""; }
}
export function setConsent(value /* "accepted" | "declined" */) {
  try { localStorage.setItem(STORAGE_KEY, value); } catch { /* private mode */ }
  if (value === "accepted") loadGoogleAnalytics();
}

export function loadGoogleAnalytics() {
  if (_loaded) return;
  const id = process.env.REACT_APP_GA_MEASUREMENT_ID;
  if (!id) return; // silently skip when GA hasn't been configured yet
  if (typeof window === "undefined") return;

  // Inject the gtag.js script
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  function gtag(){ window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag("js", new Date());
  // anonymize_ip + send_page_view:false (we'll send manually on route change)
  gtag("config", id, { anonymize_ip: true, send_page_view: false });
  _loaded = true;
}

export function trackPageView(path) {
  const id = process.env.REACT_APP_GA_MEASUREMENT_ID;
  if (!id || !_loaded || typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

export function trackEvent(name, params = {}) {
  if (!_loaded || typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", name, params);
}
